import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import { action } from '@ember/object'
import { statefulSort, type TableAction } from 'teacher-ui-client/utils/giraffe/stateful-sort'
import DataTableOperations from '@blakeelearning/data-tables/components/data-table-operations'
import { Tooltip } from 'teacher-ui-client/components/tooltip'
import { t } from 'ember-intl'
import { on } from '@ember/modifier'
import { fn } from '@ember/helper'
import { eq } from 'ember-truth-helpers'
import { formatValue } from 'teacher-ui-client/utils/format-value'
import { formatDuration } from 'teacher-ui-client/utils/format-duration'
import optional from 'teacher-ui-client/helpers/optional'
import gradeName from 'teacher-ui-client/helpers/grade-name'
import type Student from 'teacher-ui-client/models/student'
import type RouterService from '@ember/routing/router-service'
import { service } from '@ember/service'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type { SummarySchoolFpData } from 'teacher-ui-client/utils/student-events/fetchers/summary'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import type { PhosphorIconName } from 'phosphor-icons'

interface Args {
  isSubco: boolean
  fastPhonicsDashboardTableData: SummarySchoolFpData
  students: Student[]
}

interface Signature {
  Element: HTMLTableElement
  Args: Args
}

export class DashboardFastPhonicsTable extends Component<Signature> {
  @service router!: RouterService

  get studentColspan() {
    return this.args.isSubco ? 5 : 3
  }

  // The currently sorted column
  @tracked
  currentSortColumn?: string = ''

  // The sort icon to use for the currently sorted column
  @tracked
  currentSortIcon: PhosphorIconName = 'phosphor-caret-up-down-fill' // sort, sort-up, sort-down

  unsortedSortIcon: PhosphorIconName = 'phosphor-caret-up-down-fill'

  get summaryRow() {
    const totals = this.args.fastPhonicsDashboardTableData?.school
    return {
      lessonsCompleted: totals?.lessons_completed ?? null,
      lessonQuizzesTaken: totals?.lesson_quizzes_taken ?? null,
      lessonQuizzesAverageScore: totals?.lesson_quizzes_average_score ?? null,
      wordGemsEarned: totals?.word_gems_earned ?? null,
      soundGemsEarned: totals?.sound_gems_earned ?? null,
      booksRead: totals?.books_read ?? null,
      awardsEarnedGold: totals?.awards_earned_gold ?? null,
      awardsEarnedSilver: totals?.awards_earned_silver ?? null,
      awardsEarnedBronze: totals?.awards_earned_bronze ?? null,
      usageScore: totals?.usage_score ?? null,
      totalTime: totals?.total_time ?? null,
    }
  }

  get rows() {
    const statsData = this.args.fastPhonicsDashboardTableData?.students

    return this.args.students.map((student) => {
      const foundStatsDataForStudent = statsData?.find((item) => String(item?.student_id) === student.id) ?? {}
      return {
        id: student.id,
        firstName: student.firstName,
        lastName: student.lastName,
        gradePosition: student.gradePosition,
        studentExternalId: student.externalId,
        ...foundStatsDataForStudent,
        teacherName: student
          .teachersFor(SubscriptionType.Reading)
          .map((teacher) => teacher.fullName)
          .join(', '),
        schoolClasses: student
          .schoolClassesFor(SubscriptionType.Reading)
          .map((schoolClass) => schoolClass.name)
          .join(', '),
      }
    })
  }

  transitionToStudentRoute = (studentId: string) => {
    void this.router.transitionTo('reporting.ui-scope.fast-phonics.dashboard.student.table', studentId)
  }

  @action
  statefulSort(tableAction: TableAction, tableActionOperation: string, tableActionKey: string) {
    statefulSort(this, tableAction, tableActionOperation, tableActionKey)
  }

  <template>
    <DataTableOperations @data={{this.rows}} as |transformedData tableAction|>
      <table
        class="table-print-zoom-1/2 print:divide-y print:divide-black"
        data-test-dashboard-table
        exportable
        ...attributes
      >
        <thead class="sticky top-0 font-semibold print:divide-y print:divide-black">
          {{! Using box shadow, we can get a border between the header rows, even with the sticky header }}
          <tr
            class="bg-oceany-blue-150 divide-x divide-white text-center text-black shadow-[inset_0_-1px_0] shadow-white print:divide-black"
          >
            <th class="p-3" data-test-dashboard-table-students-col colspan={{this.studentColspan}}>
              {{t "reporting.dashboardTables.students"}}
            </th>
            {{! for studentExternalId column }}
            <th class="hidden" />
            <th class="p-3" colspan="3">
              {{t "reporting.dashboardTables.lessonsCompleted.fastPhonics"}}
            </th>
            <th class="p-3" colspan="2">
              {{t "reporting.dashboardTables.quizzesCompleted.fastPhonics"}}
            </th>
            <th class="p-3">
              {{t "reporting.dashboardTables.wordsLearned"}}
            </th>
            <th class="p-3">
              {{t "reporting.dashboardTables.soundsLearned"}}
            </th>
            <th class="p-3">
              {{t "reporting.dashboardTables.booksRead"}}
            </th>
            <th class="p-3" colspan="3">
              {{t "reporting.dashboardTables.certificates"}}
            </th>
            <th class="p-3 whitespace-nowrap">
              {{t "reporting.dashboardTables.usage"}}
              <span class="print:hidden">
                <PhosphorIcon @icon="phosphor-info-fill" class="text-dusty-black-500 cursor-pointer" />
                <Tooltip @text={{t "reporting.dashboardTables.usageTooltip.fastPhonics"}} role="presentation" />
              </span>
            </th>
            <th class="p-3">
              {{t "reporting.dashboardTables.timeInProgram"}}
            </th>
          </tr>
          <tr
            class="bg-oceany-blue-100 divide-x divide-white text-black shadow-[inset_0_-1px_0] shadow-white print:divide-black"
          >
            <th
              class="min-w-40 p-3 select-none"
              {{on "click" (fn this.statefulSort tableAction "sortByValue" "firstName")}}
              role="button"
            >
              <div class="flex items-center justify-between gap-1">
                {{t "reporting.dashboardTables.firstName" htmlSafe=true}}
                <PhosphorIcon
                  @icon={{if (eq this.currentSortColumn "firstName") this.currentSortIcon this.unsortedSortIcon}}
                  class="shrink-0 print:hidden"
                  role="presentation"
                />
              </div>
            </th>
            <th
              data-test-last-name-header
              class="min-w-40 p-3 select-none"
              {{on "click" (fn this.statefulSort tableAction "sortByValue" "lastName")}}
              role="button"
            >
              <div class="flex items-center justify-between gap-1">
                {{t "reporting.dashboardTables.lastName" htmlSafe=true}}
                <PhosphorIcon
                  @icon={{if (eq this.currentSortColumn "lastName") this.currentSortIcon this.unsortedSortIcon}}
                  class="shrink-0 print:hidden"
                  role="presentation"
                />
              </div>
            </th>
            <th class="hidden">
              {{t "reporting.dashboardTables.studentExternalId"}}
            </th>
            <th
              class="min-w-40 p-3 select-none"
              {{on "click" (fn this.statefulSort tableAction "sortByValue" "schoolClasses")}}
              role="button"
            >
              <div class="flex items-center justify-between gap-1">
                {{t "reporting.dashboardTables.classes"}}
                <PhosphorIcon
                  @icon={{if (eq this.currentSortColumn "schoolClasses") this.currentSortIcon this.unsortedSortIcon}}
                  class="shrink-0 print:hidden"
                  role="presentation"
                />
              </div>
            </th>
            {{#if @isSubco}}
              <th
                data-test-teacher-names-header
                class="min-w-40 p-3 select-none"
                {{on "click" (fn this.statefulSort tableAction "sortByValue" "teacherName")}}
                role="button"
              >
                <div class="flex items-center justify-between gap-1">
                  {{t "reporting.dashboardTables.teacher"}}
                  <PhosphorIcon
                    @icon={{if (eq this.currentSortColumn "teacherName") this.currentSortIcon this.unsortedSortIcon}}
                    class="shrink-0 print:hidden"
                    role="presentation"
                  />
                </div>
              </th>
              <th
                data-test-grade-position-header
                class="p-3 select-none"
                {{on "click" (fn this.statefulSort tableAction "sortByValue" "gradePosition")}}
                role="button"
              >
                <div class="flex items-center justify-between gap-1">
                  {{t "grade.label"}}
                  <PhosphorIcon
                    @icon={{if (eq this.currentSortColumn "gradePosition") this.currentSortIcon this.unsortedSortIcon}}
                    class="shrink-0 print:hidden"
                    role="presentation"
                  />
                </div>
              </th>
            {{/if}}
            <th
              class="p-3 select-none"
              {{on "click" (fn this.statefulSort tableAction "sortByNumber" "initial_lesson_number")}}
              role="button"
            >
              <div class="flex items-center justify-between gap-1">
                {{t "reporting.dashboardTables.initialLesson.fastPhonics" htmlSafe=true}}
                <PhosphorIcon
                  @icon={{if
                    (eq this.currentSortColumn "initial_lesson_number")
                    this.currentSortIcon
                    this.unsortedSortIcon
                  }}
                  class="shrink-0 print:hidden"
                  role="presentation"
                />
              </div>
            </th>
            <th
              class="p-3 select-none"
              {{on "click" (fn this.statefulSort tableAction "sortByNumber" "highest_lesson_number")}}
              role="button"
            >
              <div class="flex items-center justify-between gap-1">
                {{t "reporting.dashboardTables.highestLessonCompleted.fastPhonics" htmlSafe=true}}
                <PhosphorIcon
                  @icon={{if
                    (eq this.currentSortColumn "highest_lesson_number")
                    this.currentSortIcon
                    this.unsortedSortIcon
                  }}
                  class="shrink-0 print:hidden"
                  role="presentation"
                />
              </div>
            </th>
            <th
              class="p-3 select-none"
              {{on "click" (fn this.statefulSort tableAction "sortByNumber" "lessons_completed")}}
              role="button"
            >
              <div class="flex items-center justify-between gap-1">
                {{t "reporting.dashboardTables.totalCompleted"}}
                <PhosphorIcon
                  @icon={{if
                    (eq this.currentSortColumn "lessons_completed")
                    this.currentSortIcon
                    this.unsortedSortIcon
                  }}
                  class="shrink-0 print:hidden"
                  role="presentation"
                />
              </div>
            </th>
            <th
              class="p-3 select-none"
              {{on "click" (fn this.statefulSort tableAction "sortByNumber" "lesson_quizzes_taken")}}
              role="button"
            >
              <div class="flex items-center justify-between gap-1">
                {{t "reporting.dashboardTables.numberOfQuizzesTaken" htmlSafe=true}}
                <PhosphorIcon
                  @icon={{if
                    (eq this.currentSortColumn "lesson_quizzes_taken")
                    this.currentSortIcon
                    this.unsortedSortIcon
                  }}
                  class="shrink-0 print:hidden"
                  role="presentation"
                />
              </div>
            </th>
            <th
              class="p-3 select-none"
              {{on "click" (fn this.statefulSort tableAction "sortByNumber" "lesson_quizzes_average_score")}}
              role="button"
            >
              <div class="flex items-center justify-between gap-1">
                {{t "reporting.dashboardTables.averageLessonQuizScore.fastPhonics" htmlSafe=true}}
                <PhosphorIcon
                  @icon={{if
                    (eq this.currentSortColumn "lesson_quizzes_average_score")
                    this.currentSortIcon
                    this.unsortedSortIcon
                  }}
                  class="shrink-0 print:hidden"
                  role="presentation"
                />
              </div>
            </th>
            <th
              class="p-3 select-none"
              {{on "click" (fn this.statefulSort tableAction "sortByNumber" "word_gems_earned")}}
              role="button"
            >
              <div class="text-right">
                <PhosphorIcon
                  @icon={{if (eq this.currentSortColumn "word_gems_earned") this.currentSortIcon this.unsortedSortIcon}}
                  class="shrink-0 print:hidden"
                  role="presentation"
                />
              </div>
            </th>
            <th
              class="p-3 select-none"
              {{on "click" (fn this.statefulSort tableAction "sortByNumber" "sound_gems_earned")}}
              role="button"
            >
              <div class="text-right">
                <PhosphorIcon
                  @icon={{if
                    (eq this.currentSortColumn "sound_gems_earned")
                    this.currentSortIcon
                    this.unsortedSortIcon
                  }}
                  class="shrink-0 print:hidden"
                  role="presentation"
                />
              </div>
            </th>
            <th
              class="p-3 select-none"
              {{on "click" (fn this.statefulSort tableAction "sortByNumber" "books_read")}}
              role="button"
            >
              <div class="text-right">
                <PhosphorIcon
                  @icon={{if (eq this.currentSortColumn "books_read") this.currentSortIcon this.unsortedSortIcon}}
                  class="shrink-0 print:hidden"
                  role="presentation"
                />
              </div>
            </th>
            <th
              class="p-3 select-none"
              {{on "click" (fn this.statefulSort tableAction "sortByNumber" "awards_earned_gold")}}
              role="button"
            >
              <div class="flex items-center justify-between gap-1">
                {{t "reporting.dashboardTables.gold"}}
                <PhosphorIcon
                  @icon={{if
                    (eq this.currentSortColumn "awards_earned_gold")
                    this.currentSortIcon
                    this.unsortedSortIcon
                  }}
                  class="shrink-0 print:hidden"
                  role="presentation"
                />
              </div>
            </th>
            <th
              class="p-3 select-none"
              {{on "click" (fn this.statefulSort tableAction "sortByNumber" "awards_earned_silver")}}
              role="button"
            >
              <div class="flex items-center justify-between gap-1">
                {{t "reporting.dashboardTables.silver"}}
                <PhosphorIcon
                  @icon={{if
                    (eq this.currentSortColumn "awards_earned_silver")
                    this.currentSortIcon
                    this.unsortedSortIcon
                  }}
                  class="shrink-0 print:hidden"
                  role="presentation"
                />
              </div>
            </th>
            <th
              class="p-3 select-none"
              {{on "click" (fn this.statefulSort tableAction "sortByNumber" "awards_earned_bronze")}}
              role="button"
            >
              <div class="flex items-center justify-between gap-1">
                {{t "reporting.dashboardTables.bronze"}}
                <PhosphorIcon
                  @icon={{if
                    (eq this.currentSortColumn "awards_earned_bronze")
                    this.currentSortIcon
                    this.unsortedSortIcon
                  }}
                  class="shrink-0 print:hidden"
                  role="presentation"
                />
              </div>
            </th>
            <th
              class="p-3 select-none"
              {{on "click" (fn this.statefulSort tableAction "sortByNumber" "usage_score")}}
              role="button"
            >
              <div class="text-right">
                <PhosphorIcon
                  @icon={{if (eq this.currentSortColumn "usage_score") this.currentSortIcon this.unsortedSortIcon}}
                  class="shrink-0 print:hidden"
                  role="presentation"
                />
              </div>
            </th>
            <th
              class="p-3 select-none"
              {{on "click" (fn this.statefulSort tableAction "sortByNumber" "total_time")}}
              role="button"
            >
              <div class="flex items-center justify-between gap-1">
                {{t "reporting.dashboardTables.totalHours"}}
                <PhosphorIcon
                  @icon={{if (eq this.currentSortColumn "total_time") this.currentSortIcon this.unsortedSortIcon}}
                  class="shrink-0 print:hidden"
                  role="presentation"
                />
              </div>
            </th>
          </tr>
          <tr class="bg-oceany-blue-100 text-black shadow-[inset_0_-1px_0] shadow-white" data-test-summary-row>
            <th class="p-3" colspan={{this.studentColspan}}>
              {{t "reporting.dashboardTables.totals"}}
            </th>
            {{! for studentExternalId column }}
            <th class="hidden" />
            <td class="p-3">
              -
            </td>
            <td class="p-3">
              -
            </td>
            <td class="p-3" data-test-lessons-completed>
              {{formatValue this.summaryRow.lessonsCompleted}}
            </td>
            <td class="p-3">
              {{formatValue this.summaryRow.lessonQuizzesTaken}}
            </td>
            <td class="p-3" data-test-lesson-quizzes-avg>
              {{formatValue this.summaryRow.lessonQuizzesAverageScore append="%"}}
            </td>
            <td class="p-3">
              {{formatValue this.summaryRow.wordGemsEarned}}
            </td>
            <td class="p-3">
              {{formatValue this.summaryRow.soundGemsEarned}}
            </td>
            <td class="p-3">
              {{formatValue this.summaryRow.booksRead}}
            </td>
            <td class="p-3">
              {{formatValue this.summaryRow.awardsEarnedGold}}
            </td>
            <td class="p-3">
              {{formatValue this.summaryRow.awardsEarnedSilver}}
            </td>
            <td class="p-3">
              {{formatValue this.summaryRow.awardsEarnedBronze}}
            </td>
            <td class="p-3">
              {{formatValue this.summaryRow.usageScore}}
            </td>
            <td class="p-3" data-test-total-time>
              {{formatDuration this.summaryRow.totalTime "-"}}
            </td>
          </tr>
        </thead>
        <tbody class="print:divide-dusty-black-100 print:divide-y">
          {{#each transformedData as |row index|}}
            {{! template-lint-disable no-invalid-interactive }}
            <tr
              class="even:bg-oceany-blue-50 hover:bg-oceany-blue-100 cursor-pointer bg-white"
              data-test-table-row={{index}}
              {{on "click" (fn (optional this.transitionToStudentRoute) row.id)}}
            >
              <td class="text-oceany-blue-300 p-3 font-bold wrap-anywhere">
                {{row.firstName}}
              </td>
              <td class="text-oceany-blue-300 p-3 font-bold wrap-anywhere" data-test-last-name>
                {{row.lastName}}
              </td>
              <td class="hidden">
                {{row.studentExternalId}}
              </td>
              <td class="p-3">
                {{row.schoolClasses}}
              </td>
              {{#if @isSubco}}
                <td class="p-3" data-test-teacher-names>
                  {{row.teacherName}}
                </td>
                <td class="p-3">
                  {{gradeName row.gradePosition}}
                </td>
              {{/if}}
              <td class="p-3">
                {{formatValue row.initial_lesson_number}}
              </td>
              <td class="p-3">
                {{formatValue row.highest_lesson_number}}
              </td>
              <td class="p-3" data-test-lessons-completed>
                {{formatValue row.lessons_completed}}
              </td>
              <td class="p-3">
                {{formatValue row.lesson_quizzes_taken}}
              </td>
              <td class="p-3" data-test-lesson-quizzes-avg>
                {{formatValue row.lesson_quizzes_average_score append="%"}}
              </td>
              <td class="p-3">
                {{formatValue row.word_gems_earned}}
              </td>
              <td class="p-3">
                {{formatValue row.sound_gems_earned}}
              </td>
              <td class="p-3">
                {{formatValue row.books_read}}
              </td>
              <td class="p-3">
                {{formatValue row.awards_earned_gold}}
              </td>
              <td class="p-3">
                {{formatValue row.awards_earned_silver}}
              </td>
              <td class="p-3">
                {{formatValue row.awards_earned_bronze}}
              </td>
              <td class="p-3">
                {{formatValue row.usage_score}}
              </td>
              <td class="p-3" data-test-total-time>
                {{formatDuration row.total_time "-"}}
              </td>
            </tr>
          {{/each}}
        </tbody>
      </table>
    </DataTableOperations>
  </template>
}

export default DashboardFastPhonicsTable
